const APP_SETTINGS_LOCAL_KEY = "xphub_app_settings_v1";

export const APP_SETTINGS_DEFAULTS = Object.freeze({
  accessibility: {
    reducedMotion: false,
    highContrast: false,
    alwaysVisibleFocus: true,
    fontScale: "md",
  },
  personalization: {
    uiDensity: "comfortable",
    language: "es-ES",
    timezone: "auto",
    defaultProfileTab: "overview",
    defaultActivityStatusFilter: "all",
  },
  notifications: {
    messages: true,
    friendRequests: true,
    follows: true,
    reviews: true,
    lists: true,
    moderation: true,
    soundEnabled: false,
  },
  updatedAt: null,
});

const FONT_SCALE_VALUES = new Set(["sm", "md", "lg"]);
const DENSITY_VALUES = new Set(["compact", "comfortable", "spacious"]);
const LANGUAGE_VALUES = new Set(["es-ES", "en-US"]);
const PROFILE_TAB_VALUES = new Set(["overview", "games", "activity", "reviews", "lists", "achievements"]);
const ACTIVITY_FILTER_VALUES = new Set(["all", "Played", "Playing", "Backlog", "Wishlist"]);

function isObject(value) {
  return value && typeof value === "object" && !Array.isArray(value);
}

function normalizeBoolean(value, fallback) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "si", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off"].includes(normalized)) return false;
  }
  return fallback;
}

function normalizeEnum(value, allowed, fallback) {
  const text = String(value || "").trim();
  return allowed.has(text) ? text : fallback;
}

export function normalizeAppSettings(raw) {
  const root = isObject(raw) ? raw : {};
  const accessibility = isObject(root.accessibility) ? root.accessibility : {};
  const personalization = isObject(root.personalization) ? root.personalization : {};
  const notifications = isObject(root.notifications) ? root.notifications : {};
  const updatedAt = root.updatedAt ? new Date(root.updatedAt) : null;

  return {
    accessibility: {
      reducedMotion: normalizeBoolean(
        accessibility.reducedMotion,
        APP_SETTINGS_DEFAULTS.accessibility.reducedMotion
      ),
      highContrast: normalizeBoolean(
        accessibility.highContrast,
        APP_SETTINGS_DEFAULTS.accessibility.highContrast
      ),
      alwaysVisibleFocus: normalizeBoolean(
        accessibility.alwaysVisibleFocus,
        APP_SETTINGS_DEFAULTS.accessibility.alwaysVisibleFocus
      ),
      fontScale: normalizeEnum(
        accessibility.fontScale,
        FONT_SCALE_VALUES,
        APP_SETTINGS_DEFAULTS.accessibility.fontScale
      ),
    },
    personalization: {
      uiDensity: normalizeEnum(
        personalization.uiDensity,
        DENSITY_VALUES,
        APP_SETTINGS_DEFAULTS.personalization.uiDensity
      ),
      language: normalizeEnum(
        personalization.language,
        LANGUAGE_VALUES,
        APP_SETTINGS_DEFAULTS.personalization.language
      ),
      timezone:
        String(personalization.timezone || "").trim().slice(0, 80) ||
        APP_SETTINGS_DEFAULTS.personalization.timezone,
      defaultProfileTab: normalizeEnum(
        personalization.defaultProfileTab,
        PROFILE_TAB_VALUES,
        APP_SETTINGS_DEFAULTS.personalization.defaultProfileTab
      ),
      defaultActivityStatusFilter: normalizeEnum(
        personalization.defaultActivityStatusFilter,
        ACTIVITY_FILTER_VALUES,
        APP_SETTINGS_DEFAULTS.personalization.defaultActivityStatusFilter
      ),
    },
    notifications: {
      messages: normalizeBoolean(notifications.messages, APP_SETTINGS_DEFAULTS.notifications.messages),
      friendRequests: normalizeBoolean(
        notifications.friendRequests,
        APP_SETTINGS_DEFAULTS.notifications.friendRequests
      ),
      follows: normalizeBoolean(notifications.follows, APP_SETTINGS_DEFAULTS.notifications.follows),
      reviews: normalizeBoolean(notifications.reviews, APP_SETTINGS_DEFAULTS.notifications.reviews),
      lists: normalizeBoolean(notifications.lists, APP_SETTINGS_DEFAULTS.notifications.lists),
      moderation: normalizeBoolean(notifications.moderation, APP_SETTINGS_DEFAULTS.notifications.moderation),
      soundEnabled: normalizeBoolean(
        notifications.soundEnabled,
        APP_SETTINGS_DEFAULTS.notifications.soundEnabled
      ),
    },
    updatedAt:
      updatedAt && Number.isFinite(updatedAt.getTime()) ? updatedAt.toISOString() : null,
  };
}

export function readLocalAppSettings() {
  try {
    const raw = localStorage.getItem(APP_SETTINGS_LOCAL_KEY);
    if (!raw) return normalizeAppSettings(APP_SETTINGS_DEFAULTS);
    return normalizeAppSettings(JSON.parse(raw));
  } catch {
    return normalizeAppSettings(APP_SETTINGS_DEFAULTS);
  }
}

export function writeLocalAppSettings(settings) {
  const normalized = normalizeAppSettings(settings);
  try {
    localStorage.setItem(APP_SETTINGS_LOCAL_KEY, JSON.stringify(normalized));
  } catch {
    // noop
  }
  return normalized;
}

export function applyAppSettingsToDom(settingsRaw) {
  if (typeof document === "undefined") return;
  const settings = normalizeAppSettings(settingsRaw);
  const root = document.documentElement;

  root.dataset.xpDensity = settings.personalization.uiDensity;
  root.dataset.xpFontScale = settings.accessibility.fontScale;
  root.dataset.xpLanguage = settings.personalization.language;

  root.classList.toggle("xp-reduced-motion", Boolean(settings.accessibility.reducedMotion));
  root.classList.toggle("xp-high-contrast", Boolean(settings.accessibility.highContrast));
  root.classList.toggle("xp-focus-visible", Boolean(settings.accessibility.alwaysVisibleFocus));
}
